import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { TypeAvoir, Facture } from '../types';
import { formatEuro } from '../utils/calculations';

interface CreerAvoirModalProps {
  open: boolean;
  onClose: () => void;
  facture: Facture;
  onAvoirCreated: () => void;
}

const CreerAvoirModal: React.FC<CreerAvoirModalProps> = ({
  open,
  onClose,
  facture,
  onAvoirCreated,
}) => {
  const [typeAvoir, setTypeAvoir] = useState<TypeAvoir>(TypeAvoir.TOTAL);
  const [montantHTPartiel, setMontantHTPartiel] = useState<string>('');
  const [motif, setMotif] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const handleSubmit = async () => {
    // Validation
    if (!motif.trim()) {
      setError('Le motif est obligatoire');
      return;
    }

    if (typeAvoir === TypeAvoir.PARTIEL) {
      const montant = parseFloat(montantHTPartiel);
      if (isNaN(montant) || montant <= 0) {
        setError('Veuillez saisir un montant valide');
        return;
      }
      if (montant > facture.montantHT) {
        setError('Le montant ne peut pas dépasser le montant HT de la facture');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      // Import dynamique du service pour éviter les dépendances circulaires
      const avoirService = (await import('../services/avoirService')).default;

      const avoirData = {
        factureOriginaleId: facture.id,
        typeAvoir,
        ...(typeAvoir === TypeAvoir.PARTIEL && {
          montantHTPartiel: parseFloat(montantHTPartiel),
        }),
        motif: motif.trim(),
        notes: notes.trim() || undefined,
      };

      await avoirService.creerAvoir(avoirData);

      // Réinitialiser le formulaire
      setTypeAvoir(TypeAvoir.TOTAL);
      setMontantHTPartiel('');
      setMotif('');
      setNotes('');

      onAvoirCreated();
      onClose();
    } catch (err: any) {
      console.error('Erreur lors de la création de l\'avoir:', err);
      setError(
        err.response?.data?.message ||
        'Une erreur est survenue lors de la création de l\'avoir'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setTypeAvoir(TypeAvoir.TOTAL);
      setMontantHTPartiel('');
      setMotif('');
      setNotes('');
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Créer un avoir pour la facture {facture.numero}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* Informations de la facture */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Facture:</strong> {facture.numero}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Client:</strong> {facture.clientNom || 'Non spécifié'}
            </Typography>
            <Typography variant="body2">
              <strong>Montant HT:</strong> {formatEuro(facture.montantHT)}
            </Typography>
            <Typography variant="body2">
              <strong>Montant TTC:</strong> {formatEuro(facture.montantTTC)}
            </Typography>
          </Alert>

          {/* Type d'avoir */}
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Type d'avoir</FormLabel>
            <RadioGroup
              value={typeAvoir}
              onChange={(e) => {
                setTypeAvoir(e.target.value as TypeAvoir);
                setError('');
              }}
            >
              <FormControlLabel
                value={TypeAvoir.TOTAL}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Avoir total</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Remboursement complet de la facture ({formatEuro(facture.montantHT)} HT)
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value={TypeAvoir.PARTIEL}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Avoir partiel</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Remboursement d'un montant spécifique
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          {/* Montant partiel */}
          {typeAvoir === TypeAvoir.PARTIEL && (
            <TextField
              fullWidth
              label="Montant HT"
              type="number"
              value={montantHTPartiel}
              onChange={(e) => {
                setMontantHTPartiel(e.target.value);
                setError('');
              }}
              inputProps={{
                min: 0,
                max: facture.montantHT,
                step: 0.01,
              }}
              helperText={`Maximum: ${formatEuro(facture.montantHT)}`}
              sx={{ mb: 3 }}
              required
            />
          )}

          {/* Motif (obligatoire) */}
          <TextField
            fullWidth
            label="Motif"
            value={motif}
            onChange={(e) => {
              setMotif(e.target.value);
              setError('');
            }}
            placeholder="Ex: Retour de marchandise défectueuse, Remise commerciale, Erreur de facturation..."
            multiline
            rows={2}
            sx={{ mb: 3 }}
            required
            helperText="Le motif est obligatoire pour la conformité légale"
          />

          {/* Notes (optionnel) */}
          <TextField
            fullWidth
            label="Notes (optionnel)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Informations complémentaires..."
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />

          {/* Erreur */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Création...' : 'Créer l\'avoir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreerAvoirModal;
